import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { useMediaQuery } from "react-responsive";

import {
	helpLinks,
	aboutLinks,
	contactLinks,
	socialLinks as updatedSocialLinks,
	shadeLinks,
} from "./links";

// Icons
import MailChimpSignup from "./MailChimpSignup";
import { ArrowUp } from "lucide-react";
import { Phone } from "lucide-react";
import { Toast } from "../../screens/customize/Toast";

const Footer = () => {
	const isMobile = useMediaQuery({ maxWidth: 767 });
	const country = useSelector((state) => state.country);
	const [toast, setToast] = useState(false);

	const bounceTransition = {
		duration: 0.4,
		ease: [0.68, -0.25, 0.265, 1.25],
	};

	const footerLinks = [
		{
			title: "Window Shades",
			links: shadeLinks.sort((a, b) => a.text.localeCompare(b.text)),
		},
		{
			title: "Help",
			links: helpLinks.sort((a, b) => a.text.localeCompare(b.text)),
		},
		{
			title: "About",
			links: aboutLinks.sort((a, b) => a.text.localeCompare(b.text)),
		},
		{
			title: "Contact",
			links: contactLinks
				.sort((a, b) => a.text.localeCompare(b.text))
				.map((link) => ({
					text: link.text,
					href: link.to,
				})),
		},
	];

	const scrollToTop = () => {
		window.scrollTo({ top: 0, behavior: "smooth" });
	};

	return (
		<>
			{/* Newsletter Section */}
			<section className="bg-secondary py-16">
				<div className="container mx-auto px-4">
					<motion.div
						className="max-w-3xl mx-auto text-center"
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true }}
						transition={bounceTransition}
					>
						<MailChimpSignup />
					</motion.div>
				</div>
			</section>

			{/* Main Footer */}
			<footer className="bg-secondary text-white pt-16 pb-8">
				<div className="container mx-auto px-4">
					{isMobile ? (
						// Mobile Footer
						<div className="space-y-8">
							{/* Contact Info */}
							<div className="text-center">
								<motion.a
									href={contactLinks[2].to}
									className="inline-flex items-center gap-2 text-lg font-medium bg-primary/10 px-6 py-3 rounded-xl hover:bg-primary/20 transition-colors"
									whileHover={{ scale: 1.05 }}
									whileTap={{ scale: 0.95 }}
								>
									<Phone /> Contact Us
								</motion.a>
							</div>

							{/* Quick Links */}
							<motion.div
								className="space-y-4"
								initial={{ opacity: 0, y: 20 }}
								whileInView={{ opacity: 1, y: 0 }}
								transition={bounceTransition}
							>
								{footerLinks.map((section, index) => (
									<div key={index} className="border-b border-white/10 pb-4">
										<h3 className="text-lg font-bold mb-4">{section.title}</h3>
										<div className="space-y-3">
											{section.links.map((link, linkIndex) => (
												<motion.div
													key={linkIndex}
													whileHover={{ x: 5 }}
													transition={{ duration: 0.2 }}
												>
													<Link
														to={link.to}
														className="text-white/80 hover:text-white"
													>
														{link.text}
													</Link>
												</motion.div>
											))}
										</div>
									</div>
								))}
							</motion.div>
						</div>
					) : (
						// Desktop Footer
						<div className="grid md:grid-cols-4 gap-12">
							{/* Links Sections */}
							{footerLinks.map((section, index) => (
								<motion.div
									key={index}
									initial={{ opacity: 0, y: 20 }}
									whileInView={{ opacity: 1, y: 0 }}
									transition={{ ...bounceTransition, delay: index * 0.1 }}
								>
									<h3 className="text-lg font-bold mb-6">{section.title}</h3>
									<div className="space-y-3">
										{section.links.map((link, linkIndex) => (
											<motion.div
												key={linkIndex}
												whileHover={{ x: 5 }}
												transition={{ duration: 0.2 }}
											>
												<Link
													to={link.to}
													className="text-white/80 hover:text-primary"
												>
													{link.text}
												</Link>
											</motion.div>
										))}
									</div>
								</motion.div>
							))}
						</div>
					)}
					<div className="mt-12 text-center flex gap-4 flex-col">
						<span className="text-white/60">Follow Us</span>
						<div className="flex justify-center gap-6">
							{updatedSocialLinks.map((link, index) => (
								<motion.a
									key={index}
									href={link.to}
									target="_blank"
									rel="noopener noreferrer"
									className="text-white/80 hover:text-primary"
									whileHover={{ scale: 1.2, rotate: 5 }}
									whileTap={{ scale: 0.9 }}
								>
									<link.Icon />
								</motion.a>
							))}
						</div>
					</div>

					{/* Bottom Bar */}
					<motion.div
						className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center"
						initial={{ opacity: 0 }}
						whileInView={{ opacity: 1 }}
						transition={{ duration: 0.6 }}
					>
						<div className="flex items-center gap-4 mb-4 md:mb-0">
							<img
								src="/images/logoDark-1.png"
								alt="Off Cut Shades"
								className="h-8"
							/>
							<p className="text-white/60 text-sm">
								All prices are in {country === "Canada" ? "CAD" : "USD"}.
								&copy;2025 Off Cut Shades.
							</p>
						</div>

						<motion.button
							onClick={scrollToTop}
							className="flex items-center gap-2 text-white/60 hover:text-white"
							whileHover={{ y: -5 }}
							whileTap={{ y: 0 }}
						>
							<span>Back to top</span>
							<ArrowUp />
						</motion.button>
					</motion.div>
				</div>
			</footer>

			{/* Toast Notification */}
			<Toast
				show={toast}
				message="Thank you for subscribing to our newsletter!"
				oonClose={() => setToast(false)}
			/>
		</>
	);
};

export default Footer;
